import type { FacilityNode, KioskMe, KioskResolvedProfile } from "@arc/contracts";
import { isWithin } from "../facilities/facility-tree.js";

type ResolvedDepartment = KioskResolvedProfile["departments"][number];

/**
 * What the kiosk screen shows, resolved from one `GET /kiosk/me` + the department the operator picked (ADR-036 §3).
 *
 * Every shape here is a screen a device can RECOVER from on its own, because nobody can easily walk to a kiosk: a
 * profile that was deleted, a second profile appearing in a realm that used to have one, a profile whose facilities
 * the account can no longer view. None of them is an error state.
 */
export type KioskLanding =
  /** The realm has no kiosk profile at all — a SuperAdmin has to create one; the screen says so. */
  | { kind: "unconfigured" }
  /** Pick a profile: the device has no id, or several exist, or the one it remembered is gone (`missing`). */
  | { kind: "pick"; available: readonly { id: number; name: string }[]; missing: boolean }
  /** The profile resolved but has no department left with a viewable facility. */
  | { kind: "empty"; profile: KioskResolvedProfile }
  /** More than one department ⇒ the legacy's "Wähle dein Department" chooser. */
  | { kind: "departments"; profile: KioskResolvedProfile }
  /** A department is in play — one department skips the chooser entirely (ADR-036 D2). */
  | { kind: "department"; profile: KioskResolvedProfile; department: ResolvedDepartment; soleDepartment: boolean };

/**
 * `chosenDepartmentId` is the operator's tap, or `null` before they have tapped. A chosen id that no longer exists
 * (the profile was edited under the device) falls back to the chooser instead of a blank screen.
 */
export function resolveKioskLanding(me: KioskMe, chosenDepartmentId: number | null): KioskLanding {
  if (me.profile === null) {
    // `requestedProfileMissing` is the only case that needs wording of its own; an empty realm is "unconfigured"
    // whether or not an id was asked for.
    if (me.available.length === 0) return { kind: "unconfigured" };
    return { kind: "pick", available: me.available, missing: me.requestedProfileMissing };
  }
  const profile = me.profile;
  if (profile.departments.length === 0) return { kind: "empty", profile };
  // One department is not a choice — the legacy special-cased this too, landing straight on the facilities.
  if (profile.departments.length === 1) {
    return { kind: "department", profile, department: profile.departments[0]!, soleDepartment: true };
  }
  const chosen = chosenDepartmentId === null ? null : (profile.departments.find((d) => d.id === chosenDepartmentId) ?? null);
  if (chosen === null) return { kind: "departments", profile };
  return { kind: "department", profile, department: chosen, soleDepartment: false };
}

/**
 * The department's facilities as the kiosk's TOP level: a facility whose ancestor is also listed is dropped, because
 * it is already reachable by drilling into that ancestor and would otherwise appear twice at different depths.
 *
 * The legacy did this with a direct-parent check only (`GetGenericUserTopFacilities`), which leaked a grandchild
 * listed alongside its grandparent. Here it is full ancestry via the `ltree` path, so the level is genuinely a level.
 */
export function kioskTopFacilities(facilities: readonly FacilityNode[]): FacilityNode[] {
  return facilities.filter((f) => !facilities.some((other) => other.id !== f.id && isWithin(f.path, other.path)));
}

/**
 * The breadcrumb chain for a drilled-into facility, TRUNCATED AT THE DEPARTMENT'S OWN TOP facility. A kiosk must never
 * offer a crumb pointing above its scope: the ancestor above a department root is a place the profile did not put this
 * device, and the legacy's breadcrumb was simply made un-clickable (`pointer-events: none`) rather than scoped, which
 * left the wrong path on screen.
 */
export function kioskAncestors(
  node: FacilityNode,
  byId: ReadonlyMap<number, FacilityNode>,
  topIds: ReadonlySet<number>,
): FacilityNode[] {
  const chain: FacilityNode[] = [];
  if (topIds.has(node.id)) return chain; // a department root has no crumbs of its own
  let pid = node.parentId;
  while (pid !== null) {
    const parent = byId.get(pid);
    if (!parent) break; // an absent ancestor ends the chain rather than rendering a gap a kiosk can't use
    chain.unshift(parent);
    if (topIds.has(parent.id)) break; // the department's own root — never walk past it
    pid = parent.parentId;
  }
  return chain;
}

/** Is `node` inside the department's scope? Guards a stale `?arc_drill` from a previous department. */
export function isInKioskScope(node: FacilityNode, tops: readonly FacilityNode[]): boolean {
  return tops.some((t) => isWithin(node.path, t.path));
}
